"use client"

/**
 * The two MRI packages as cards, shared by /pricing and the full-body MRI
 * test page on the membership-first site, so a change lands on both
 * (Eugene, 30.09: "reuse the component with an override"). Each card: the
 * scanner render, name and line, the plan's promises with the doctor's
 * call, what it includes, then the price and two equal buttons.
 *
 * Two switches. `price`: "on-top" says what the scan adds to the plan, for
 * /pricing where the plan card sits above; "split" shows the member price
 * with the one-time figure struck through, as the MRI test page's hero
 * does, for a page without the plan card. `checkUp`: with the builder's
 * state the first button is Add; without it the card books its scan.
 * Needs a PlanPricesProvider above it.
 */

import { useState, type ReactNode } from "react"
import { blobMediaUrl } from "@/lib/blob-url"
import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"
import { SCAN_PRICE_KEY_FOR_SLUG } from "@/lib/pricing"
import { useCountry } from "@/components/layout/country-provider"
import { InfoIcon } from "@/components/ui/icons"
import { ArrowRightIcon } from "@/components/ui/button"
import Link from "next/link"
import { useLocalePath } from "@/lib/use-localized-navigation"
import {
  AddButton,
  BookmarkIcon,
  PlanSummary,
  ChoiceSheet,
  DetailsPill,
  HAIRLINE,
  HoverTip,
  InfoSheet,
  ItemPicture,
  SELECT_GLOW,
  useInfoContent,
  type InfoLabels,
} from "./pricing-v5-booking"
import { mriDetails } from "./pricing-v5-items"
import { MriPromises } from "./pricing-v5-plan-card"
import { LPA_GIFT_KEY, LpaGiftPill, useLpaGift } from "../lpa-gift"
import { usePricingV5Copy } from "./pricing-v5-copy"
import type { PricingV5Data } from "./pricing-v5-page-data"
import type { BookingScan } from "../home/home-v4-data"
import { BaselinePlanCard } from "./pricing-v5-plan-card"
import {
  BookButton,
  IMAGING_KEYS,
  Included,
  OneTimeLink,
  PlanPricesProvider,
  useCheckUp,
  usePlanPrices,
  type CheckUp,
  type Copy,
} from "./pricing-v5-shared"

export type MriPrice = "on-top" | "split"

/** The popups' buttons. */
export function infoLabels({
  INFO,
  PICK,
}: Pick<Copy, "INFO" | "PICK">): InfoLabels {
  return {
    close: INFO.close,
    add: PICK.add,
    added: PICK.added,
    saveTip: PICK.saveTip,
    savedTip: PICK.savedTip,
  }
}

/** A market that cannot sell an item says so where its price or pick is. */
export function StatusPill({ label }: { label: string }) {
  return (
    <span className="type-body-md inline-flex h-6 items-center rounded-full bg-neutral-100 px-2.5 text-neutral-600">
      {label}
    </span>
  )
}

type MriPackage = Copy["MRI"]["packages"][number]

/**
 * One MRI package as a near-square card, two cards joined by 2px as in the
 * booking. Above, the scan's render, its name and line, what it includes and
 * "More info". Below, what it costs with the plan in the booking plan card's
 * words, "Baseline + Full-body MRI", the plan a year and the scan once, and
 * the Add button (Eugene, 30.09: "a bit taller, more information, a real
 * button"). The card itself picks nothing. One package at most, so Add on
 * the other swaps them.
 */
function MriPlanCard({
  pkg,
  copy,
  checkUp,
  price,
  track,
  stacked,
}: {
  pkg: MriPackage
  copy: Copy
  checkUp?: CheckUp
  price: MriPrice
  track: string
  /** Three packages: the render above the name from md, so the name and
   *  its line keep the card's width (12.10). */
  stacked?: boolean
}) {
  const { MRI, PLAN, INFO } = copy
  const { money, onTop, list, availability, biomarkersOf } = usePlanPrices()
  const { code: country } = useCountry()
  const labels = infoLabels(copy)
  const read = useInfoContent()
  const [open, setOpen] = useState(false)
  const details = mriDetails(pkg.id)
  const state = availability(details.gate)
  const status =
    state === "not_available"
      ? INFO.notAvailable
      : state === "coming_soon" || state === "waitlist"
        ? INFO.comingSoon
        : undefined
  const picked = checkUp?.mri === pkg.id
  const toggle = () => checkUp?.toggleMri(pkg.id)
  const adds = money(onTop(pkg.priceKey))
  const content = details.info
    ? read(details.info, {
        name: pkg.name,
        description: pkg.desc,
        keyBenefits: pkg.bullets,
        price: { now: `+ ${adds}`, after: MRI.onTop },
        banner: status,
        biomarkers: biomarkersOf(details.info.cmsPackageId),
      })
    : undefined

  return (
    // Four rows on the list's subgrid — head, promises, inclusions, price —
    // so cards side by side keep their icon rows and price floors level
    // whatever their copy's length (Eugene, 12.10).
    <li className="row-span-4 grid grid-rows-subgrid gap-y-0">
      <div
        className={cn(
          "relative row-span-4 grid grid-rows-subgrid gap-y-0 rounded-2xl border bg-white transition-[border-color,box-shadow] duration-200",
          picked
            ? cn("border-light-blue-400", SELECT_GLOW)
            : cn("border-transparent", HAIRLINE)
        )}
      >
        {/* The bookmark in the top right corner (Eugene, 01.10). */}
        {checkUp && !status && (
          <AddButton
            iconOnly
            picked={picked}
            onToggle={toggle}
            labels={labels}
            name={pkg.name}
            size="md"
            className="absolute top-3 right-3 sm:top-4 sm:right-4"
          />
        )}
        {/* Top-aligned, so the cards' names stand level whatever their
            lines' length. */}
        <div
          className={cn(
            "flex items-start gap-4 px-5 pt-5 sm:px-8 sm:pt-7",
            stacked && "md:flex-col md:gap-3"
          )}
        >
          {/* The booking's scanner render, shot on white and set on the
                card's white, pulled into the padding as there. */}
          <ItemPicture
            src={details.image}
            px={128}
            kind={details.kind}
            className={cn(
              "-my-2 -ml-2 aspect-[720/709] w-24 sm:w-32",
              stacked && "md:w-32"
            )}
          />
          <div className={cn("min-w-0", checkUp && "pr-8")}>
            <h3 className="type-headline-sm-emphasized text-neutral-900">
              {pkg.name}
            </h3>
            <p className="type-body-lg mt-1 text-neutral-700">{pkg.desc}</p>
          </div>
        </div>
        {/* The promises the homepage card shows with the MRI on, then
            what the package includes (Eugene, 30.09). Pro's first line
            says it holds the whole full-body MRI, so it leads, in medium
            weight. */}
        <MriPromises
          PLAN={PLAN}
          MRI={MRI}
          className="mx-5 mt-4 border-b border-neutral-100 pb-3 sm:mx-8"
        />
        <div className="px-5 pt-4 pb-5 sm:px-8 sm:pb-7 [&>ul]:gap-3">
          <Included
            items={pkg.cardBullets}
            size="lg"
            strongFirst={pkg.id !== "core"}
          />
        </div>
        {/* One card, the price floor set off by a line inside its padding
            (Eugene, 30.09: the split into two blocks looked off). */}
        <div>
          <div aria-hidden className="mx-5 h-px bg-neutral-100 sm:mx-8" />
          <div className="flex flex-col gap-4 px-5 py-5 sm:px-8 sm:py-6">
            {status ? (
              <div className="flex flex-wrap items-center gap-3">
                <StatusPill label={status} />
                {content && (
                  <DetailsPill
                    label={INFO.more}
                    onClick={() => setOpen(true)}
                    size="lg"
                  />
                )}
              </div>
            ) : (
              <>
                {price === "split" ? (
                  // The member price the way the hero shows it, with the
                  // one-time figure struck through and the (i) for both ways
                  // to book (Eugene, 01.10).
                  <MemberPrice pkg={pkg} copy={copy} track={track} />
                ) : (
                  // What the scan adds to the plan, once (Eugene, 30.09: "+
                  // 1'991 one time", then "just one time"). The plan's own
                  // price is on its card above.
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <span className="type-body-xl-emphasized sm:type-headline-sm-emphasized whitespace-nowrap text-neutral-900">
                      + {adds}
                    </span>
                    {/* The price without the plan, struck, as on the test
                      pages: the member saving was missing here (Willem,
                      bugbash 14.10). */}
                    {list(pkg.priceKey) != null && (
                      <s className="type-body-lg whitespace-nowrap text-neutral-400">
                        <span className="sr-only">{MRI.priceOneOff} </span>
                        {formatNumber(list(pkg.priceKey)!, country)}
                      </s>
                    )}
                    <span className="type-body-lg text-neutral-500">
                      {MRI.oneTime}
                    </span>
                  </p>
                )}
                {/* Book and More info, two halves of one row; the scan
                  holds the Baseline blood test, so it books straight away
                  and saving is the bookmark in the corner (Eugene, 01.10). */}
                <div className={cn("grid gap-3", content ? "grid-cols-2" : "")}>
                  <BookButton
                    scan={pkg.scan}
                    label={MRI.book}
                    trackingLabel={`${track}_book_${pkg.id}`}
                    trackingLocation={track}
                    arrow={false}
                    className="block min-w-0"
                    buttonClassName="h-11 w-full min-w-0 whitespace-nowrap px-3 sm:px-5 md:px-5"
                  />
                  {content && (
                    <button
                      type="button"
                      aria-haspopup="dialog"
                      onClick={() => setOpen(true)}
                      className="type-title-md inline-flex h-11 min-w-0 items-center justify-center rounded-full border border-transparent bg-neutral-100 px-3 whitespace-nowrap text-neutral-900 transition-colors duration-200 hover:bg-neutral-200/60 sm:px-5"
                    >
                      {INFO.more}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {content && (
        <InfoSheet
          content={content}
          open={open}
          onClose={() => setOpen(false)}
          labels={labels}
          action={status || !checkUp ? undefined : { picked, onToggle: toggle }}
        />
      )}
    </li>
  )
}

/**
 * One scan's popup without its card, the same one its More info opens. The
 * homepage's membership card opens it from the MRI strip, so "Learn more"
 * keeps the visitor on the page like the plan's own (Vega, bugbash 14.10).
 * Needs a PlanPricesProvider above it.
 */
export function MriInfoSheet({
  id,
  open,
  onClose,
}: {
  id: MriPackage["id"]
  open: boolean
  onClose: () => void
}) {
  const copy = usePricingV5Copy()
  const { money, onTop, biomarkersOf } = usePlanPrices()
  const read = useInfoContent()
  const pkg = copy.MRI.packages.find((p) => p.id === id)
  const info = mriDetails(id).info
  if (!pkg || !info) return null
  const content = read(info, {
    name: pkg.name,
    description: pkg.desc,
    keyBenefits: pkg.bullets,
    price: { now: `+ ${money(onTop(pkg.priceKey))}`, after: copy.MRI.onTop },
    biomarkers: biomarkersOf(info.cmsPackageId),
  })
  return (
    <InfoSheet
      content={content}
      open={open}
      onClose={onClose}
      labels={infoLabels(copy)}
    />
  )
}

/** The cards side by side from lg, 8px apart (Eugene, 30.09); three since
 *  Core came back as the plain full-body MRI (12.10). */
export function MriPlanCards({
  copy,
  checkUp,
  price = "on-top",
  track,
  className,
}: {
  copy: Copy
  checkUp?: CheckUp
  price?: MriPrice
  track: string
  className?: string
}) {
  return (
    <ul
      className={cn(
        "grid gap-2",
        // Three packages: a card is never narrower than a phone's (340px),
        // so three in a row only where they fit, else two or one (12.10).
        copy.MRI.packages.length > 2
          ? "grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))]"
          : "lg:grid-cols-2",
        className
      )}
    >
      {copy.MRI.packages.map((pkg) => (
        <MriPlanCard
          key={pkg.id}
          pkg={pkg}
          copy={copy}
          checkUp={checkUp}
          price={price}
          track={track}
          stacked={copy.MRI.packages.length > 2}
        />
      ))}
    </ul>
  )
}

/** Gives a page's MRI section one price feed, when the page has one. */
/** The film series of the hormone test's steps (data/tests.ts), for the
 *  plan site's blood test pages. */
export const PLAN_STEP_IMAGES: [string, string, string] = [
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/01-before-v1.png"
  ),
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/02-clinic-v1.png"
  ),
  blobMediaUrl(
    "/images/marketing/hormone-women-film-20260930/03-report-v1.png"
  ),
]

export function MriPlanScope({
  data,
  children,
}: {
  data?: PricingV5Data
  children: ReactNode
}) {
  if (!data) return <>{children}</>
  return <PlanPricesProvider data={data}>{children}</PlanPricesProvider>
}

/** "From + {price} one time, on top of your Baseline plan.": what the
 *  first package adds to the plan. Inside MriPlanScope. */
export function MriPlanFrom() {
  const { MRI } = usePricingV5Copy()
  const { money, onTop } = usePlanPrices()
  return <>{MRI.pageSub(money(onTop(MRI.packages[0].priceKey)))}</>
}

/** The test page hero's price: "From {price}" with the one-time figure
 *  struck through, then "Price for Ahead members" and an (i) that opens
 *  the two ways to book, with the plan and one time, each to its own
 *  booking (Eugene, 30.09 and 01.10). Inside MriPlanScope. */
export function MriPlanHeroPrice({
  fromLabel,
  included,
  pkgId,
  own,
  caption,
  dark,
  track,
}: {
  /** "From" before the price; left out for one package's own price. */
  fromLabel?: string
  /** A scan the MRI holds: this line, the MRI's price after it in
   *  brackets. */
  included?: string
  /** The package whose price shows; the first one when left out. */
  pkgId?: MriPackage["id"]
  /** A scan the package holds, with a price of its own: that price shows,
   *  the popup books the package. */
  own?: OwnPrice
  caption?: string
  dark?: boolean
  track: string
}) {
  const copy = usePricingV5Copy()
  const pkg =
    copy.MRI.packages.find((p) => p.id === pkgId) ?? copy.MRI.packages[0]
  return (
    <MemberPrice
      pkg={pkg}
      copy={copy}
      fromLabel={included ? undefined : fromLabel}
      included={included}
      own={own}
      caption={caption}
      dark={dark}
      track={track}
      size="hero"
    />
  )
}

type ChoiceLines = Parameters<typeof PlanSummary>[0]["lines"]
type Offer = {
  description?: string
  benefits?: string[]
  lines: ChoiceLines
  total: { label: string; value: string }
}

/** A price with the plan, the one-time figure struck through beside it,
 *  then a caption and an (i) that opens what the price holds: the plan's
 *  card to book, when it renews, and booking once as a link under it. The hero shows it with
 *  "From" at the price's size but lighter, the cards a step smaller
 *  (Eugene, 30.09 and 01.10). The MRI and the blood test pages share it.
 *  Inside MriPlanScope. */
export function PlanChoicePrice({
  copy,
  price,
  was,
  unit,
  caption,
  scan,
  plan,
  fromLabel,
  included,
  dark,
  track,
  size = "card",
}: {
  copy: Copy
  price: string
  /** The one-time price, struck through; left out when it is no higher. */
  was?: number | null
  /** A unit after the price, light, as "/year". */
  unit?: string
  caption: string
  scan: BookingScan
  plan: Offer
  fromLabel?: string
  /** In place of the price: what holds the test, the price after it in
   *  brackets, nothing struck through (Eugene, 01.10: "Included in the
   *  full-body MRI", the price in brackets). */
  included?: string
  dark?: boolean
  track: string
  size?: "hero" | "card"
}) {
  const { MRI, PLAN } = copy
  const { code: country } = useCountry()
  // The struck one-time price is a bare figure, no currency code.
  const figure = (amount: number | null) =>
    amount == null ? "—" : formatNumber(amount, country)
  const [open, setOpen] = useState(false)
  const hero = size === "hero"
  return (
    <div className={cn("flex flex-col", dark && "items-center")}>
      {included ? (
        <p
          className={cn(
            "flex flex-wrap items-baseline gap-x-2",
            dark ? "justify-center text-center text-white" : "text-neutral-900"
          )}
        >
          <span
            className={hero ? "type-headline-md-emphasized" : "type-title-lg"}
          >
            {included}
          </span>
          <span
            className={cn(
              "whitespace-nowrap",
              hero ? "type-headline-md" : "type-body-xl",
              dark ? "text-white/70" : "text-neutral-500"
            )}
          >
            ({price})
          </span>
        </p>
      ) : (
        <p
          className={cn(
            "flex flex-wrap items-baseline gap-x-2",
            dark ? "text-white" : "text-neutral-900"
          )}
        >
          {fromLabel && <span className="type-headline-lg">{fromLabel}</span>}
          <span
            className={cn(
              "font-semibold whitespace-nowrap",
              hero
                ? "type-headline-lg-emphasized"
                : "type-headline-sm-emphasized"
            )}
          >
            {price}
          </span>
          {unit && (
            <span
              className={cn(
                "-ml-1 font-normal whitespace-nowrap",
                hero ? "type-headline-sm" : "type-body-xl",
                dark ? "text-white/70" : "text-neutral-500"
              )}
            >
              {unit}
            </span>
          )}
          {was !== undefined && (
            <s
              className={cn(
                "font-normal whitespace-nowrap",
                hero ? "type-headline-sm" : "type-body-xl",
                dark ? "text-white/50" : "text-neutral-400"
              )}
            >
              <span className="sr-only">{MRI.priceOneOff} </span>
              {figure(was)}
            </s>
          )}
        </p>
      )}
      <div className={cn("flex items-center gap-0.5", "mt-1")}>
        <p
          className={cn(
            hero ? "type-body-lg" : "type-body-md",
            dark ? "text-white/70" : "text-neutral-500"
          )}
        >
          {caption}
        </p>
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={MRI.priceInfo}
          className={cn(
            "-my-2 inline-flex size-9 shrink-0 items-center justify-center rounded-full transition-colors",
            dark
              ? "text-white/80 hover:text-white"
              : "text-neutral-500 hover:text-neutral-900"
          )}
        >
          <InfoIcon size={20} />
        </button>
      </div>
      <ChoiceSheet
        title={MRI.chooseTitle}
        open={open}
        onClose={() => setOpen(false)}
        closeLabel={infoLabels(copy).close}
      >
        {/* What the price holds, one way to book it: the one-time check-up
            at the same price beside it made the plan read as a catch
            (Sven, AHD-13799, 08.10). It stays a quiet link under the
            button. */}
        <PlanSummary {...plan} />
        <BookButton
          scan={scan}
          label={MRI.bookWithPlan}
          trackingLabel={`${track}_choose_plan`}
          trackingLocation={track}
          size="lg"
          arrow={false}
          className="mt-6 block w-full"
          buttonClassName="w-full"
        />
        <p className="type-body-md mt-4 text-center text-pretty text-neutral-600">
          {PLAN.cancelNote}
        </p>
        <OneTimeLink
          scan={scan}
          trackingLabel={`${track}_choose_one_time`}
          trackingLocation={track}
          className="type-body-md mx-auto mt-3 block w-fit text-neutral-900 underline decoration-neutral-300 underline-offset-4 hover:decoration-neutral-900"
        >
          {PLAN.oneTimeShort}
        </OneTimeLink>
      </ChoiceSheet>
    </div>
  )
}

type OwnPrice = { price: number | null; was: number | null }

/** An MRI package's price with the plan: what the scan adds on top of it. */
function MemberPrice({
  pkg,
  copy,
  fromLabel,
  included,
  own,
  caption,
  dark,
  track,
  size = "card",
}: {
  pkg: MriPackage
  copy: Copy
  fromLabel?: string
  included?: string
  /** In place of the package's price (and its struck one); the popup stays
   *  the package's. */
  own?: OwnPrice
  /** In place of "Price for Ahead members". */
  caption?: string
  dark?: boolean
  track: string
  size?: "hero" | "card"
}) {
  const { MRI, PLAN } = copy
  const { money, onTop, list, plan } = usePlanPrices()
  const scan = onTop(pkg.priceKey)
  const firstYear = scan == null || plan == null ? null : scan + plan
  return (
    <PlanChoicePrice
      copy={copy}
      price={money(own ? own.price : onTop(pkg.priceKey))}
      was={own ? (own.was ?? undefined) : list(pkg.priceKey)}
      caption={caption ?? MRI.heroMembers}
      scan={pkg.scan}
      fromLabel={fromLabel}
      included={included}
      dark={dark}
      track={track}
      size={size}
      plan={{
        description: MRI.optionPlanDesc,
        // The scan by name, then what the plan holds: the blood test, the
        // doctor's plan, member prices (Sven, AHD-13799, and Eugene, 08.10
        // and 09.10).
        benefits: [
          MRI.benefit(pkg.id, pkg.name),
          PLAN.included[0],
          PLAN.included[1],
          PLAN.included[2],
        ],
        // The scan at the member price the visitor clicked from, then the
        // plan, then the first year's total (Eugene, 01.10).
        lines: [
          {
            label: pkg.name,
            note: MRI.oneTime,
            value: money(onTop(pkg.priceKey)),
          },
          {
            label: PLAN.sheetName,
            note: PLAN.perYearShort,
            value: money(plan),
          },
        ],
        // The scan plus the membership: Advanced and Pro's member price, and
        // Core's member price with the membership on top, as it has no blood
        // test of its own.
        total: { label: MRI.priceTotal, value: money(firstYear) },
      }}
    />
  )
}

/** The blood test page's hero price on the membership-first site: the
 *  plan a year, and the same choice popup as the MRI's, the plan or one
 *  blood test with no renewal (Eugene, 01.10). One blood test costs what a
 *  year of the plan does, so nothing is struck through. Inside
 *  MriPlanScope. */
export function BloodPlanHeroPrice({
  dark,
  track,
  addonKey,
}: {
  dark?: boolean
  track: string
  /** A single blood test's page (its `pricing.addon*` key): the hero shows
   *  that test on top of the plan, and both ways to book carry the Baseline
   *  blood test with it (Eugene, 01.10: "the same on every blood test"). */
  addonKey?: string
}) {
  const copy = usePricingV5Copy()
  const { MRI, PLAN, ADDONS } = copy
  const { money, list, member, plan: planAmount } = usePlanPrices()
  const gift = useLpaGift()
  const plan = money(planAmount)
  const addon = addonKey
    ? ADDONS.groups
        .flatMap((group): { key: string; name: string }[] => group.items)
        .find((item) => item.key === addonKey)
    : undefined
  if (addonKey && addon) {
    const price = (
      <AddonPlanPrice
        name={addon.name}
        onTop={member(addonKey)}
        regular={list(addonKey)}
        dark={dark}
        track={track}
      />
    )
    // In Switzerland a new membership gets Lp(a) free (AHD-14738): its
    // page keeps the test's price and says so on a pill under it (Eugene,
    // 03.11).
    if (!gift || addonKey !== LPA_GIFT_KEY) return price
    return (
      <div className={cn("flex flex-col gap-3", dark && "items-center")}>
        {price}
        <LpaGiftPill text={gift.pill} label={gift.mark} />
      </div>
    )
  }
  return (
    <PlanChoicePrice
      copy={copy}
      price={plan}
      unit={PLAN.perYearShort}
      // The MRI's words, "Price for Ahead members" (Eugene, 01.10).
      caption={MRI.heroMembers}
      scan="blood"
      dark={dark}
      track={track}
      size="hero"
      plan={{
        // What the plan holds: the blood test, the doctor's plan, member
        // prices (Sven, AHD-13799, and Eugene, 08.10).
        benefits: [PLAN.included[0], PLAN.included[1], PLAN.included[2]],
        lines: [
          { label: PLAN.sheetName, note: PLAN.perYearShort, value: plan },
        ],
        total: { label: MRI.priceTotal, value: plan },
      }}
    />
  )
}

/** What a test costs with the plan and once: summed, or unknown when a
 *  figure is. */
const sum = (...amounts: (number | null)[]) =>
  amounts.some((a) => a == null)
    ? null
    : amounts.reduce<number>((t, a) => t + (a ?? 0), 0)

/**
 * A single test's hero price on the plan site: the test at the member
 * price with its regular one struck through, and the choice popup. Both
 * ways to book carry what the test goes with: the Baseline blood test,
 * and for a scan the first MRI package as well, the scan holding the blood
 * test (Eugene, 01.10: "the same as the blood add-ons, with the MRI's
 * specifics"). Inside MriPlanScope.
 */
function AddonPlanPrice({
  name,
  onTop,
  regular,
  withMri = false,
  mriPkgId = "advanced",
  dark,
  track,
}: {
  name: string
  onTop: number | null
  regular: number | null
  withMri?: boolean
  /** The MRI package the scan goes with in the popup. */
  mriPkgId?: MriPackage["id"]
  dark?: boolean
  track: string
}) {
  const copy = usePricingV5Copy()
  const { MRI, PLAN } = copy
  const prices = usePlanPrices()
  const { money, plan: planAmount } = prices
  const pkg = MRI.packages.find((p) => p.id === mriPkgId) ?? MRI.packages[0]
  const benefits = [PLAN.included[0], PLAN.included[1], PLAN.included[2]]
  const test = (value: number | null) => ({
    label: name,
    note: MRI.oneTime,
    value: money(value),
  })
  if (withMri) {
    return (
      <PlanChoicePrice
        copy={copy}
        price={money(onTop)}
        was={regular ?? undefined}
        caption={MRI.heroMembers}
        scan={pkg.scan}
        dark={dark}
        track={track}
        size="hero"
        plan={{
          description: MRI.optionPlanDesc,
          // The MRI the scan goes with, by name, then what the plan holds.
          benefits: [MRI.benefit(pkg.id, pkg.name), ...benefits],
          // The scan, the MRI it goes with, then the plan, as the MRI's
          // own popup.
          lines: [
            test(onTop),
            {
              label: pkg.name,
              note: MRI.oneTime,
              value: money(prices.onTop(pkg.priceKey)),
            },
            {
              label: PLAN.sheetName,
              note: PLAN.perYearShort,
              value: money(planAmount),
            },
          ],
          // The scan, the MRI on top of the plan, the plan: for Advanced
          // and Pro the MRI's member price, for Core (no blood test of its
          // own) its member price and the plan.
          total: {
            label: MRI.priceTotal,
            value: money(sum(onTop, prices.onTop(pkg.priceKey), planAmount)),
          },
        }}
      />
    )
  }
  return (
    <PlanChoicePrice
      copy={copy}
      price={money(onTop)}
      was={regular ?? undefined}
      caption={MRI.heroMembers}
      scan="blood"
      dark={dark}
      track={track}
      size="hero"
      plan={{
        benefits,
        // The test at its member price, then the plan it goes with.
        lines: [
          test(onTop),
          {
            label: PLAN.sheetName,
            note: PLAN.perYearShort,
            value: money(planAmount),
          },
        ],
        total: { label: MRI.priceTotal, value: money(sum(onTop, planAmount)) },
      }}
    />
  )
}

type ScanFallback = { regular?: number; member?: number }

/** A scan's price with the plan and its regular one: its add-on key's (or
 *  the add-on its page shares, SCAN_PRICE_KEY_FOR_SLUG), else the page's
 *  static figures. Inside MriPlanScope. */
function useScanPrice(
  slug: string,
  addonKey?: string,
  fallback?: ScanFallback
) {
  const { member, list } = usePlanPrices()
  const key = addonKey ?? SCAN_PRICE_KEY_FOR_SLUG[slug]
  const onTop =
    (key ? member(key) : null) ?? fallback?.member ?? fallback?.regular ?? null
  const regular = (key ? list(key) : null) ?? fallback?.regular ?? null
  return { onTop, regular }
}

/**
 * Above a scan's title on the plan site, for a scan bought with the plain
 * full-body MRI: "Add-on to Full-body MRI", for one only Pro holds:
 * "Included in Full-body MRI Pro"; a link to the MRI's page. Grey
 * text alone; on hover a faint pill and an arrow (Eugene, 24.10). Nothing
 * while the scan has no price of its own. Inside MriPlanScope.
 */
export function ScanAddOnLink({
  slug,
  addonKey,
  fallback,
  dark,
}: {
  slug: string
  addonKey?: string
  fallback?: ScanFallback
  dark?: boolean
}) {
  const { MRI } = usePricingV5Copy()
  const lp = useLocalePath()
  const { onTop } = useScanPrice(slug, addonKey, fallback)
  // A scan only Pro holds comes with Pro (brain volumetry, 24.10); one
  // with its own price goes with the plain MRI. DEXA says where to check
  // insurance instead (Eugene, 24.10).
  const label = SCANS_IN_PRO.has(slug)
    ? MRI.scanIncludedPro
    : (SCANS_ADDED_TO_CORE.has(slug) || SCANS_BOOKED_WITH_MRI.has(slug)) &&
        onTop != null
      ? MRI.addOnTag
      : null
  if (label == null) return null
  return (
    <HeroEyebrowLink href={lp("/tests/full-body-mri")} dark={dark}>
      {label}
    </HeroEyebrowLink>
  )
}

/**
 * The plan site's line above a test's title: grey text that links on; on
 * hover a faint pill and an arrow (Eugene, 24.10). -ml-3 lines the text up
 * with the title, mb-1 keeps it with the title.
 */
function HeroEyebrowLink({
  href,
  dark,
  children,
}: {
  href: string
  dark?: boolean
  children: ReactNode
}) {
  return (
    <Link
      href={href}
      className={cn(
        "group type-body-md mb-1 -ml-3 inline-flex w-fit items-center rounded-full px-3 py-1.5 font-medium transition-colors duration-200",
        dark
          ? "text-white/75 hover:bg-white/10 hover:text-white"
          : "text-neutral-500 hover:bg-neutral-900/[0.04] hover:text-neutral-900"
      )}
    >
      {children}
      <ArrowRightIcon
        size={16}
        className="ml-0 w-0 shrink-0 opacity-0 transition-all duration-200 group-hover:ml-1 group-hover:w-4 group-hover:opacity-100"
      />
    </Link>
  )
}

/** Above a blood add-on's title on the plan site (Lp(a), cortisol, PSA,
 *  hormones, …): "Add-on to Ahead check-up", a link to the plan's page, in
 *  place of the blue "Add-on" badge (Eugene, 24.10). */
export function BloodAddOnLink({
  dark,
  addonKey,
}: {
  dark?: boolean
  /** The page's test: Lp(a) in Switzerland says it is included
   *  (AHD-14738). */
  addonKey?: string
}) {
  const { MRI } = usePricingV5Copy()
  const lp = useLocalePath()
  const gift = useLpaGift()
  return (
    <HeroEyebrowLink href={lp("/pricing")} dark={dark}>
      {gift && addonKey === LPA_GIFT_KEY ? (
        <span data-lpa-gift="">{gift.includedIn}</span>
      ) : (
        MRI.addOnBloodTag
      )}
    </HeroEyebrowLink>
  )
}

/**
 * A scan's page on the plan site, a scan that goes with an MRI: its price
 * on top, the popup with the MRI in both ways to book (Eugene, 01.10).
 * A scan the MRI holds: the package that holds it, its price in brackets
 * (brain volumetry: Pro, Eugene, 23.10). `fallback` is the page's static figures, for a
 * scan the price feed does not carry. Inside MriPlanScope.
 */
export function ImagingPlanHeroPrice({
  slug,
  addonKey,
  name,
  fallback,
  fromLabel,
  dark,
  track,
}: {
  /** The page's test slug, to tell a scan the MRI holds. */
  slug: string
  addonKey?: string
  name: string
  fallback?: ScanFallback
  fromLabel: string
  dark?: boolean
  track: string
}) {
  const { MRI } = usePricingV5Copy()
  const { onTop, regular } = useScanPrice(slug, addonKey, fallback)
  // Held by Pro alone, and booked with it: the scan's own price (Sven's
  // PIM), the popup Pro's, what the visitor books; without a price, Pro's
  // with its caption naming Pro. The link above the title says the scan
  // comes with Pro, the buttons book it (Eugene, 24.10).
  if (SCANS_IN_PRO.has(slug))
    return onTop != null ? (
      <MriPlanHeroPrice
        pkgId="pro"
        own={{ price: onTop, was: regular === onTop ? null : regular }}
        dark={dark}
        track={track}
      />
    ) : (
      <MriPlanHeroPrice
        pkgId="pro"
        caption={MRI.heroMembersPro}
        dark={dark}
        track={track}
      />
    )
  // Its own price; the link above the title says it goes with the
  // full-body MRI (ScanAddOnLink, Eugene, 24.10).
  if (SCANS_ADDED_TO_CORE.has(slug) && onTop != null)
    return (
      <AddonPlanPrice
        name={name}
        onTop={onTop}
        regular={regular === onTop ? null : regular}
        withMri
        mriPkgId="core"
        dark={dark}
        track={track}
      />
    )
  // Held by the full-body MRI: the MRI's price, said so above it.
  if (SCANS_IN_MRI.has(slug))
    return (
      <MriPlanHeroPrice
        fromLabel={fromLabel}
        included={MRI.scanIncluded}
        pkgId="advanced"
        dark={dark}
        track={track}
      />
    )
  if (onTop == null)
    return <MriPlanHeroPrice fromLabel={fromLabel} dark={dark} track={track} />
  return (
    <AddonPlanPrice
      name={name}
      onTop={onTop}
      regular={regular === onTop ? null : regular}
      withMri
      dark={dark}
      track={track}
    />
  )
}

/**
 * The scans Advanced and Pro hold: the gender health scan (prostate or
 * ovaries) and body composition. Since Core is back (12.10) they are
 * add-ons to it with a price of their own (SCANS_ADDED_TO_CORE); this is
 * the hero for when the feed has no price for them.
 */
const SCANS_IN_MRI = new Set([
  "prostate-mri",
  "pelvic-mri",
  "gender-health",
  "body-composition",
])

/** Scans with a price of their own, bought with the plain full-body MRI
 *  and held by bigger packages (body composition: Advanced and Pro; brain
 *  volumetry: Pro): the price leads, the MRI it is bought with under it,
 *  the page's description says which packages hold it (Eugene, 23.10).
 *  Without a price they read as SCANS_IN_MRI or SCANS_IN_PRO. */
const SCANS_ADDED_TO_CORE = new Set([
  "body-composition",
  "gender-health",
  "prostate-mri",
  "pelvic-mri",
])

/** Scans no package holds, booked with the full-body MRI on the plan site
 *  (Eugene, 24.10). */
const SCANS_BOOKED_WITH_MRI = new Set(["calcium-score", "mamma-mri"])

/** The scans only Pro holds. Brain volumetry has no booking of its own (its
 *  add-on product is archived): its page shows its PIM price, and its
 *  buttons book Pro (test-detail-page.tsx). */
const SCANS_IN_PRO = new Set(["brain-volumetry"])

/** The steps for a scan's page on the plan site: before, the scan, the
 *  results, as /pricing's how it works (Eugene, 01.10). */
export const IMAGING_PLAN_STEP_IMAGES: [string, string, string] = [
  PLAN_STEP_IMAGES[0],
  blobMediaUrl("/images/mri/walkthrough/step-scan.png"),
  PLAN_STEP_IMAGES[2],
]

/** The homepage's Baseline plan card, as /pricing shows it, for the blood
 *  test page on the membership-first site (Eugene, 01.10). Inside
 *  MriPlanScope. `largeNote` is the blood ad pages' bigger line under it. */
export function BloodPlanCard({
  track,
  largeNote,
}: {
  track: string
  largeNote?: boolean
}) {
  const copy = usePricingV5Copy()
  return (
    <BaselinePlanCard
      copy={copy}
      cta={{ label: copy.SUMMARY.ctaShort, scan: "blood" }}
      track={track}
      largeNote={largeNote}
    />
  )
}

/** The MRI cards' three promises, for the MRI test page's hero on the
 *  membership-first site (Eugene, 30.09). */
export function MriHeroPromises({
  tone,
  className,
}: {
  tone?: "light" | "dark"
  className?: string
}) {
  const { PLAN, MRI } = usePricingV5Copy()
  return <MriPromises PLAN={PLAN} MRI={MRI} tone={tone} className={className} />
}

/** The same row for the blood test pages' heroes, with the blood card's
 *  third promise in place of the doctor's call, which only the MRI has
 *  (Eugene, 01.10). */
export function BloodHeroPromises({
  tone,
  className,
}: {
  tone?: "light" | "dark"
  className?: string
}) {
  const { PLAN, MRI } = usePricingV5Copy()
  return (
    <MriPromises
      PLAN={PLAN}
      MRI={MRI}
      tone={tone}
      blood
      className={className}
    />
  )
}

/** The two cards with the split price, each booking its scan. Inside
 *  MriPlanScope. The full-body MRI test page uses it on the
 *  membership-first site. */
export function MriPlanCardsSplit({
  track,
  className,
}: {
  track: string
  className?: string
}) {
  const copy = usePricingV5Copy()
  return (
    <MriPlanCards
      copy={copy}
      price="split"
      track={track}
      className={className}
    />
  )
}

/**
 * Save beside the hero's booking button on a single test's page: the
 * bookmark the MRI cards carry, as a round button a step lower than the
 * booking button, saving the test to the same check-up as the lists and
 * the navbar (Eugene, 01.10). A light grey bookmark on a faint fill,
 * blue once saved; on the phone's
 * photo hero white. A scan saves the full-body MRI with it when none is
 * saved yet, as it goes with one. Nothing for a test the lists do not
 * carry.
 */
export function HeroSaveButton({
  itemKey,
  dark,
  className,
}: {
  itemKey?: string
  dark?: boolean
  className?: string
}) {
  const { ADDONS, PICK, MRI } = usePricingV5Copy()
  const checkUp = useCheckUp()
  // Nothing to save for the Lp(a) the plan includes (AHD-14738).
  const gift = useLpaGift()
  const item = ADDONS.groups
    .flatMap((group): { key: string; name: string }[] => group.items)
    .find((entry) => entry.key === itemKey)
  if (!item || (gift && item.key === LPA_GIFT_KEY)) return null
  const picked = checkUp.tests.includes(item.key)
  return (
    <button
      type="button"
      aria-pressed={picked}
      aria-label={`${picked ? PICK.added : PICK.add} ${item.name}`}
      onClick={() => {
        if (!picked && IMAGING_KEYS.has(item.key) && !checkUp.mri)
          checkUp.toggleMri(
            (MRI.packages.find((p) => p.id === "advanced") ?? MRI.packages[0])
              .id,
            { auto: true }
          )
        checkUp.toggleTest(item.key)
      }}
      className={cn(
        "group/tip relative inline-flex size-12 shrink-0 items-center justify-center rounded-full transition-colors duration-200",
        // A faint fill so it does not float, darker on hover (Eugene,
        // 01.10).
        dark
          ? "bg-white/10 hover:bg-white/20"
          : "bg-neutral-50 hover:bg-neutral-100",
        className
      )}
    >
      <HoverTip label={picked ? PICK.savedTip : PICK.saveTip} />
      <BookmarkIcon
        filled
        className={cn(
          "size-6 transition-colors duration-200",
          dark
            ? picked
              ? "text-white"
              : "text-white/50 group-hover/tip:text-white/80"
            : picked
              ? "text-deep-blue-700"
              : "text-neutral-300 group-hover/tip:text-neutral-400"
        )}
      />
    </button>
  )
}
